// A trust configuration's public fields as a form reads and writes them
// (02-DESIGN/01-identity/01-tenants-and-roles.md § the trust configuration):
// the issuer, the client ids, the organisation, role, handle, identity and
// display claims, the scopes and where people may sign in. Shared by the
// workspace's own sign-in and by a child's blueprint, whose sign-in section
// is exactly these fields pushed down as the managed provider
// (02-DESIGN/13-blueprints/01-the-blueprint.md § sign-in). Pure, so that the
// mapping is tested without a page.

import { allows, Surface } from "../../platform/profile.ts";
import type { ProviderConfig } from "../../wire/auth.ts";

/** ProviderDraft is the public fields as typed. */
export interface ProviderDraft {
  issuer: string;
  clientId: string;
  browserClientId: string;
  terminalClientId: string;
  orgClaim: string;
  orgValue: string;
  subjectClaim: string;
  roleClaim: string;
  handleClaim: string;
  displayClaim: string;
  scopes: string;
  browser: boolean;
  terminal: boolean;
  desktop: boolean;
}

/** PROVIDER_FORM_KEYS are the configuration's fields the draft holds; every other field passes through untouched. */
export const PROVIDER_FORM_KEYS: readonly (keyof ProviderConfig)[] = [
  "issuer",
  "client_id",
  "client_ids",
  "organisation",
  "subject_claim",
  "role_claim",
  "handle_claim",
  "display_claim",
  "scopes",
  "surfaces",
];

/** providerDraftOf is the form filled from a configuration; a new one allows every surface. */
export function providerDraftOf(c: Partial<ProviderConfig> | undefined): ProviderDraft {
  return {
    issuer: c?.issuer ?? "",
    clientId: c?.client_id ?? "",
    browserClientId: c?.client_ids?.browser ?? "",
    terminalClientId: c?.client_ids?.terminal ?? "",
    orgClaim: c?.organisation?.name ?? "",
    orgValue: c?.organisation?.value ?? "",
    subjectClaim: c?.subject_claim ?? "",
    roleClaim: c?.role_claim ?? "",
    handleClaim: c?.handle_claim ?? "",
    displayClaim: c?.display_claim ?? "",
    scopes: (c?.scopes ?? []).join(" "),
    browser: c === undefined || allows(c, Surface.browser),
    terminal: c === undefined || allows(c, Surface.terminal),
    desktop: c === undefined || allows(c, Surface.desktop),
  };
}

/**
 * providerFieldsOf is the public fields from the form, each present only
 * where typed. The surfaces are always listed, except that everywhere is
 * left unlisted when `unlistedEverywhere` — a configuration naming no
 * surfaces allows every one — so that a document that listed none reads
 * back as it was.
 */
export function providerFieldsOf(d: ProviderDraft, unlistedEverywhere = false): Partial<ProviderConfig> {
  const c: Partial<ProviderConfig> = { issuer: d.issuer.trim() };
  if (d.clientId.trim() !== "") {
    c.client_id = d.clientId.trim();
  }
  const ids: Record<string, string> = {};
  if (d.browserClientId.trim() !== "") {
    ids.browser = d.browserClientId.trim();
  }
  if (d.terminalClientId.trim() !== "") {
    ids.terminal = d.terminalClientId.trim();
  }
  if (Object.keys(ids).length > 0) {
    c.client_ids = ids;
  }
  if (d.orgClaim.trim() !== "") {
    c.organisation = { name: d.orgClaim.trim(), value: d.orgValue.trim() };
  }
  if (d.subjectClaim.trim() !== "") {
    c.subject_claim = d.subjectClaim.trim();
  }
  if (d.roleClaim.trim() !== "") {
    c.role_claim = d.roleClaim.trim();
  }
  if (d.handleClaim.trim() !== "") {
    c.handle_claim = d.handleClaim.trim();
  }
  if (d.displayClaim.trim() !== "") {
    c.display_claim = d.displayClaim.trim();
  }
  const scopes = d.scopes.split(/\s+/).filter((x) => x !== "");
  if (scopes.length > 0) {
    c.scopes = scopes;
  }
  const surfaces: string[] = [];
  if (d.browser) {
    surfaces.push(Surface.browser);
  }
  if (d.terminal) {
    surfaces.push(Surface.terminal);
  }
  if (d.desktop) {
    surfaces.push(Surface.desktop);
  }
  if (!(unlistedEverywhere && surfaces.length === 3)) {
    c.surfaces = surfaces;
  }
  return c;
}

/** nowhere reports whether the form allows no surface: leaving every box empty would read as everywhere, so one is asked for. */
export function nowhere(d: ProviderDraft): boolean {
  return !d.browser && !d.terminal && !d.desktop;
}
